import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";
import { getProfileData } from "../User/ProfileSlice";

/**
 * OTP Redux Slice
 * 
 * This slice manages all OTP-related operations including:
 * - Profile OTP (Email, WhatsApp)
 * - Service OTP (WhatsApp)
 * 
 * Structure:
 * - Profile OTP: Email and WhatsApp verification for user profiles
 * - Service OTP: WhatsApp verification for service phone numbers
 */

const initialState = {
  errors: null,
  isLoading: false,
  // Profile OTP states
  profileOTP: {
    email: {
      isSending: false,
      isVerifying: false,
    },
    whatsapp: {
      isSending: false,
      isVerifying: false,
    },
  },
  // Service OTP states
  serviceOTP: {
    whatsapp: {
      isSending: false,
      isVerifying: false,
    },
  },
};

const otpSlice = createSlice({
  name: "otp",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    // Profile OTP actions
    setProfileEmailSending: (state, action) => {
      state.profileOTP.email.isSending = action.payload;
    },
    setProfileEmailVerifying: (state, action) => {
      state.profileOTP.email.isVerifying = action.payload;
    },
    setProfileWhatsAppSending: (state, action) => {
      state.profileOTP.whatsapp.isSending = action.payload;
    },
    setProfileWhatsAppVerifying: (state, action) => {
      state.profileOTP.whatsapp.isVerifying = action.payload;
    },
    // Service OTP actions
    setServiceWhatsAppSending: (state, action) => {
      state.serviceOTP.whatsapp.isSending = action.payload;
    },
    setServiceWhatsAppVerifying: (state, action) => {
      state.serviceOTP.whatsapp.isVerifying = action.payload;
    },
    clearErrors: (state) => {
      state.errors = null;
    },
  },
});

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format phone number with country code (91 for India)
 */
const formatPhoneWithCountryCode = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('91') && cleaned.length >= 12) {
    return cleaned;
  }
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }
  if (!cleaned.startsWith('91')) {
    cleaned = '91' + cleaned;
  }
  return cleaned;
};

/**
 * Extract 10-digit phone number (remove country code)
 */
const extractTenDigitPhone = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    cleaned = cleaned.substring(2);
  }
  return cleaned;
};

/**
 * Validate email format
 */
const validateEmail = (email) => {
  return email && email.includes('@');
};


/**
 * Extract field error from error message
 */
const extractFieldError = (errorMsg, fieldType) => {
  const errorMessage = errorMsg.toLowerCase();
  const fieldKeywords = {
    email: ['email'],
    whatsapp: ['whatsapp', 'whats app'],
    phone: ['phone', 'number'],
  };

  const keywords = fieldKeywords[fieldType] || [];
  const hasFieldKeyword = keywords.some(keyword => errorMessage.includes(keyword));
  const hasDuplicateKeyword = errorMessage.includes('already') ||
    errorMessage.includes('registered') ||
    errorMessage.includes('exist');

  if (hasFieldKeyword && hasDuplicateKeyword) {
    return {
      field: fieldType === 'whatsapp' ? 'whatsappNumber' : fieldType,
      message: errorMsg,
    };
  }
  return null;
};

// ============================================
// PROFILE OTP OPERATIONS
// ============================================

/**
 * Send OTP to email for profile email verification
 */
export const sendProfileEmailOTP = (email) => async (dispatch) => {
  dispatch(setProfileEmailSending(true));
  dispatch(clearErrors());

  try {
    if (!validateEmail(email)) {
      const errorMsg = "Please enter a valid email address";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    const URL = "client/myProfile/email/otp/request";
    const response = await axiosInstance.post(URL, { email });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "OTP sent to your email address",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to send OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to send OTP. Please try again.";

    const fieldError = extractFieldError(errorMsg, 'email');

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg, fieldError };
  } finally {
    dispatch(setProfileEmailSending(false));
  }
};

/**
 * Verify email with OTP for profile
 */
export const verifyProfileEmailOTP = (email, otp) => async (dispatch) => {
  dispatch(setProfileEmailVerifying(true));
  dispatch(clearErrors());

  try {
    if (!validateEmail(email)) {
      const errorMsg = "Invalid email format.";
      showNotification({ message: errorMsg, type: "error" });
      return { success: false, message: errorMsg };
    }

    const URL = "client/myProfile/email/otp/verify";
    const response = await axiosInstance.post(URL, { email, otp });
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      // Refresh profile data to get updated verification status
      dispatch(getProfileData());
      showNotification({
        message: message || "Email verified successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message, data };
    } else {
      showNotification({
        message: message || "OTP verification failed",
        theme: "light",
        type: "error",
      });
      return { success: false, message };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Invalid OTP. Please try again.";
    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setProfileEmailVerifying(false));
  }
};

/**
 * Send OTP to WhatsApp for profile WhatsApp verification
 */
export const sendProfileWhatsAppOTP = (whatsappNumber) => async (dispatch) => {
  dispatch(setProfileWhatsAppSending(true));
  dispatch(clearErrors());

  try {
    const formattedPhone = formatPhoneWithCountryCode(whatsappNumber);

    if (!formattedPhone || formattedPhone.length < 12) {
      const errorMsg = "Please enter a valid WhatsApp number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    const URL = "client/myProfile/whatsapp/otp/request";
    const response = await axiosInstance.post(URL, { whatsappNumber: formattedPhone });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "OTP sent to your WhatsApp number",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to send OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to send OTP. Please try again.";

    const fieldError = extractFieldError(errorMsg, 'whatsapp');

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg, fieldError };
  } finally {
    dispatch(setProfileWhatsAppSending(false));
  }
};

/**
 * Verify WhatsApp number with OTP for profile
 */
export const verifyProfileWhatsAppOTP = (whatsappNumber, otp) => async (dispatch) => {
  dispatch(setProfileWhatsAppVerifying(true));
  dispatch(clearErrors());

  try {
    const formattedPhone = formatPhoneWithCountryCode(whatsappNumber);

    if (!formattedPhone || formattedPhone.length < 12) {
      const errorMsg = "Please enter a valid WhatsApp number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    const URL = "client/myProfile/whatsapp/otp/verify";
    const response = await axiosInstance.post(URL, {
      whatsappNumber: formattedPhone,
      otp: otp
    });
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      // Refresh profile data to get updated verification status
      dispatch(getProfileData());
      showNotification({
        message: message || "WhatsApp number verified successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message, data };
    } else {
      const errorMsg = message || "Failed to verify OTP";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message ||
      error.message ||
      "Failed to verify OTP. Please try again.";

    dispatch(setErrors({
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      message: errorMsg
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setProfileWhatsAppVerifying(false));
  }
};

// ============================================
// SERVICE OTP OPERATIONS
// ============================================

/**
 * Send OTP to WhatsApp for service phone verification
 * @param {string|number} serviceId - Service ID (optional for new services)
 * @param {string} phoneNumber - 10-digit phone number
 */
export const sendServiceWhatsAppOTP = (serviceId, phoneNumber) => async (dispatch) => {
  dispatch(setServiceWhatsAppSending(true));
  dispatch(clearErrors());
  

  try {
    // Extract 10-digit phone number
    const cleanedPhone = extractTenDigitPhone(phoneNumber);

    if (!cleanedPhone || cleanedPhone.length !== 10) {
      const errorMsg = "Please enter a valid 10-digit phone number";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }

    // Use service-specific endpoint if serviceId is provided, otherwise use generic endpoint
    const URL = serviceId
      ? `serviceProvider/services/service/${serviceId}/whatsapp/otp/request`
      : "serviceProvider/services/service/phone/otp/request";

    
    const requestBody = serviceId
      ? { whatsappNumber: cleanedPhone }
      : { phone: formatPhoneWithCountryCode(phoneNumber) };

    const response = await axiosInstance.post(URL, requestBody);
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "OTP sent successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message, data };
    } else {
      showNotification({
        message: message || "Failed to send OTP",
        theme: "light",
        type: "error",
      });
      return { success: false, message };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || "Failed to send OTP";
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    dispatch(setErrors({ message: errorMsg }));
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setServiceWhatsAppSending(false));
  }
};

/**
 * Verify service phone number with OTP
 * @param {string|number|null} serviceId - Service ID (null for service creation)
 * @param {string} phoneNumber - Phone number (10-digit or formatted)
 * @param {string} otp - 6-digit OTP
 */
export const verifyServiceWhatsAppOTP = (serviceId, phoneNumber, otp) => async (dispatch) => {
  dispatch(setServiceWhatsAppVerifying(true));
  dispatch(clearErrors());

  try {
    // Use service-specific endpoint if serviceId is provided, otherwise use generic endpoint for service creation
    const URL = serviceId
      ? `serviceProvider/services/service/${serviceId}/whatsapp/otp/verify`
      : "serviceProvider/services/service/phone/otp/verify";

    let requestBody;

    if (serviceId) {
      // For existing services: send 10-digit WhatsApp number
      const cleanedPhone = extractTenDigitPhone(phoneNumber);

      if (!cleanedPhone || cleanedPhone.length !== 10) {
        const errorMsg = "Invalid WhatsApp number";
        showNotification({
          message: errorMsg,
          theme: "light",
          type: "error",
        });
        return { success: false, message: errorMsg };
      }

      requestBody = {
        whatsappNumber: cleanedPhone,
        otp: otp
      };
    } else {
      // For service creation: send formatted phone with country code
      const formattedPhone = formatPhoneWithCountryCode(phoneNumber);

      if (!formattedPhone || formattedPhone.length < 12) {
        const errorMsg = "Invalid phone number format";
        showNotification({
          message: errorMsg,
          theme: "light",
          type: "error",
        });
        return { success: false, message: errorMsg };
      }

      requestBody = {
        phone: formattedPhone,
        otp: otp
      };
    }

    const response = await axiosInstance.post(URL, requestBody);
    const { status, message, data } = response?.data || {};

    if (status === "success") {
      // Refresh service details to get updated verification status (only for existing services)
      if (serviceId) {
        // Import getBusinessDetails if needed - check if it's available
        // dispatch(getBusinessDetails(serviceId, true));
      }
      showNotification({
        message: message || "WhatsApp number verified successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message, data };
    } else {
      showNotification({
        message: message || "OTP verification failed",
        theme: "light",
        type: "error",
      });
      return { success: false, message };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || "Invalid OTP. Please try again.";
    dispatch(setErrors({ message: errorMsg }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setServiceWhatsAppVerifying(false));
  }
};

// ============================================
// EXPORTS
// ============================================

export const {
  setErrors,
  setLoading,
  setProfileEmailSending,
  setProfileEmailVerifying,
  setProfileWhatsAppSending,
  setProfileWhatsAppVerifying,
  setServiceWhatsAppSending,
  setServiceWhatsAppVerifying,
  clearErrors,
} = otpSlice.actions;

export default otpSlice.reducer;
